import {
  LocalizationProvider,
  DateRangePicker as MuiDateRangePicker,
  SingleInputDateRangeField,
  type FieldType,
} from '@mui/x-date-pickers-pro';
import type {
  DateRangePickerProps as MuiDateRangePickerProps,
  SingleInputDateRangeFieldProps,
  PickerValidDate,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import type { Dayjs } from 'dayjs';
import { forwardRef, type Ref, type RefAttributes } from 'react';

import { Icon } from '../icon/Icon';

type DateRange = [Date | null, Date | null];
type DayjsRange = [Dayjs | null, Dayjs | null];

type DateRangePickerPropsWithDate = Omit<
  MuiDateRangePickerProps<Dayjs>,
  'value' | 'onChange' | 'defaultValue' | 'minDate' | 'maxDate'
> & {
  id?: string;
  value?: DateRange;
  onChange?: (dates: DateRange) => void;
  onClear?: () => void;
  defaultValue?: DateRange;
  minDate?: Date | null;
  maxDate?: Date | null;
  size?: 'small' | 'medium';
};

type FieldComponent = (<TDate extends PickerValidDate>(
  props: SingleInputDateRangeFieldProps<TDate> &
    RefAttributes<HTMLInputElement>,
) => JSX.Element) & { fieldType?: FieldType };

const WrappedSingleInputDateRangeField = forwardRef(
  (
    props: SingleInputDateRangeFieldProps<Dayjs>,
    ref: Ref<HTMLInputElement>,
  ) => {
    return <SingleInputDateRangeField size="small" {...props} ref={ref} />;
  },
) as FieldComponent;

WrappedSingleInputDateRangeField.fieldType = 'single-input';

const adapter = new AdapterDayjs();

export const DateRangePicker = ({
  disabled = false,
  format = 'MM/DD/YYYY',
  value,
  onChange,
  onClear,
  defaultValue,
  minDate,
  maxDate,
  ...rest
}: DateRangePickerPropsWithDate) => {
  const convertToDateRange = (dayjsRange: DayjsRange): DateRange => [
    dayjsRange[0] ? adapter.toJsDate(dayjsRange[0].startOf('day')) : null,
    dayjsRange[1] ? adapter.toJsDate(dayjsRange[1].startOf('day')) : null,
  ];

  const convertToDayjsRange = (
    dateRange: DateRange = [null, null],
  ): DayjsRange => [
    dateRange[0]
      ? adapter.date(
          dateRange[0] instanceof Date
            ? dateRange[0].toISOString()
            : dateRange[0],
        )
      : null,
    dateRange[1]
      ? adapter.date(
          dateRange[1] instanceof Date
            ? dateRange[1].toISOString()
            : dateRange[1],
        )
      : null,
  ];

  const handleRangeChange = (newValue: DayjsRange) => {
    // Wait until both dates are selected before calling onChange
    if (newValue[0] && newValue[1]) {
      onChange?.(convertToDateRange(newValue));
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MuiDateRangePicker
        sx={{ minWidth: '300px' }}
        disabled={disabled}
        format={format}
        value={value ? convertToDayjsRange(value) : [null, null]}
        onChange={handleRangeChange}
        defaultValue={
          defaultValue ? convertToDayjsRange(defaultValue) : [null, null]
        }
        minDate={minDate ? adapter.date(minDate.toISOString()) : undefined}
        maxDate={maxDate ? adapter.date(maxDate.toISOString()) : undefined}
        closeOnSelect={false}
        slots={{
          field: WrappedSingleInputDateRangeField,
        }}
        slotProps={{
          textField: {
            InputProps: {
              endAdornment: <Icon>event</Icon>,
            },
          },
          field: {
            clearable: !!onClear,
            onClear,
          },
        }}
        {...rest}
      />
    </LocalizationProvider>
  );
};
